import { useEffect, useMemo, useRef, useState } from "react";
import { array, bool, func, object, shape, string } from "prop-types";
import dayjs from "dayjs";
import styled from "styled-components/macro";
import { rem } from "polished";

import { BREAKPOINT_WRAPPER, routes } from "../../../constants";
import { listingDesktopStyles } from "../../../styles/Styles.listing.desktop";
import { listingMobileStyles } from "../../../styles/Styles.listing.mobile";

import Alert from "../../../styles/components/Alert";
import { CardGrid } from "../../../styles/components/Card";
import { Link } from "react-router-dom";

import ResultsListing from "../../results/components/Results.listing";
import CardGridSkeleton from "../../../styles/components/CardGridSkeleton";
import FadeInTransition from "../../../styles/components/FadeInTransition";

const ListingsLink = ({ zip }) => (
  <Link to={`${routes.results.path}?locationZip=${zip}&distance=30`}>
    See all listings in this area
  </Link>
);

const LocationRecentListings = ({
  addToComparedListings,
  authenticated,
  comparedListings,
  fetchSearchResults,
  location,
  query,
  removeFromComparedListings,
  searchError,
  searchLoading,
  searchResults,
  targetDevice,
  trackEvent,
  trackModalView,
  userWatchedListings,
}) => {
  const isMounted = useRef(true);

  const [listings, setListings] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  const analyticsLabel = "location";

  // Check if search results match query
  const found = useMemo(() => {
    if (
      !!searchResults &&
      !Array.isArray(searchResults) && // shape changed from array to object
      searchResults.query === query &&
      searchResults.authenticated === authenticated &&
      dayjs().isBefore(searchResults.expires)
    ) {
      return searchResults;
    } else {
      return null;
    }
  }, [authenticated, query, searchResults]);

  // Fetch search results
  useEffect(() => {
    if (isMounted.current && !searchError && !searchLoading && !!query) {
      if (!found) {
        setIsFetching(true);
        fetchSearchResults(query);
      } else {
        setListings(found.results);
        setIsFetching(false);
      }
    }
  }, [fetchSearchResults, found, query, searchError, searchLoading]);

  // Cleanup when unmounted
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <RecentListings>
      <header>
        <h2>Recent Listings in {location.locationCity}</h2>
        <p>
          <ListingsLink zip={location.locationZip} />
        </p>
      </header>
      <div>
        {!!searchError && <Alert type="error">{searchError}</Alert>}
        {isFetching && (
          <CardGridSkeleton
            isIn={isFetching}
            numCards={6}
            theme={targetDevice === "mobile" ? "dark" : "light"}
          />
        )}

        <FadeInTransition isIn={!!listings?.length && !isFetching}>
          <CardGrid>
            {!searchError &&
              !isFetching &&
              !!listings &&
              !!listings.length &&
              listings.map((listing, index) => (
                <Listing
                  addToComparedListings={addToComparedListings}
                  analyticsLabel={analyticsLabel}
                  authenticated={authenticated}
                  border="bottom"
                  comparedListings={comparedListings}
                  key={`${listing.equipmentId}-${index}`}
                  listing={listing}
                  removeFromComparedListings={removeFromComparedListings}
                  targetDevice={targetDevice}
                  trackEvent={trackEvent}
                  trackModalView={trackModalView}
                  userWatchedListings={userWatchedListings}
                />
              ))}
          </CardGrid>
        </FadeInTransition>
        {!searchError && !isFetching && !!listings && !listings.length && (
          <Alert>No listings found</Alert>
        )}
      </div>
      <footer>
        <p>
          <ListingsLink zip={location.locationZip} />
        </p>
      </footer>
    </RecentListings>
  );
};

LocationRecentListings.propTypes = {
  addToComparedListings: func.isRequired,
  authenticated: bool,
  comparedListings: array.isRequired,
  fetchSearchResults: func.isRequired,
  location: shape({
    locationCity: string,
    locationZip: string,
  }),
  query: string,
  removeFromComparedListings: func.isRequired,
  searchError: string,
  searchLoading: bool,
  searchResults: object,
  targetDevice: string,
  trackEvent: func.isRequired,
  trackModalView: func.isRequired,
  userWatchedListings: array,
};

const RecentListings = styled.aside`
  margin-left: auto;
  margin-right: auto;
  padding-left: ${rem("30px")};
  padding-right: ${rem("30px")};

  header,
  footer {
    line-height: 1.2;
    text-align: center;

    p {
      font-size: ${rem("13px")};
    }

    > :last-child {
      margin-bottom: 0;
    }
  }

  header {
    margin-bottom: ${rem("26px")};

    h2 {
      margin-bottom: ${rem("2px")};
    }
  }

  footer {
    padding-top: ${rem("23px")};
  }

  @media ${BREAKPOINT_WRAPPER} {
    padding-left: ${rem("60px")};
    padding-right: ${rem("60px")};

    header,
    footer {
      p {
        font-size: ${rem("16px")};
      }
    }

    header {
      margin-bottom: ${rem("50px")};

      h2 {
        margin-bottom: ${rem("9px")};
      }
    }

    footer {
      padding-top: ${rem("24px")};
    }
  }

  @media (min-width: 1000px) {
    // max-width: ${rem("814px")};
  }
`;

const Listing = styled(ResultsListing)`
  ${({ targetDevice }) => {
    return targetDevice === "mobile"
      ? listingMobileStyles
      : listingDesktopStyles;
  }}

  .listing-title > a {
    color: inherit !important;
    text-decoration: none !important;
  }
`;

export default LocationRecentListings;
